import { useEffect, useCallback, useRef, memo, type ReactNode } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

// Elements considered focusable for the dialog's focus trap / initial focus.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface BaseFormModalProps {
  open: boolean;
  title?: string;
  theme?: string;
  dialogClass?: string;
  bodyClass?: string;
  saveText?: string;
  cancelText?: string;
  showSave?: boolean;
  showCancel?: boolean;
  saveButtonClass?: string;
  saveDisabled?: boolean;
  busy?: boolean;
  scrollLock?: boolean;
  hint?: string;
  subheader?: ReactNode;
  /**
   * Custom action-bar content rendered in the pinned footer (outside the scrollable
   * body). When provided, replaces the built-in Save/Cancel buttons entirely — use
   * this when a modal needs extra actions (e.g., a third button) that must stay
   * visible while the body scrolls.
   */
  footer?: ReactNode;
  onSave?: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

export const BaseFormModal = memo(function BaseFormModal({
  open,
  title = 'Modal',
  theme: themeProp,
  dialogClass = '',
  bodyClass = '',
  saveText = 'Save',
  cancelText = 'Cancel',
  showSave = true,
  showCancel = true,
  saveButtonClass = 'kstc-btn--primary',
  saveDisabled = false,
  busy = false,
  scrollLock = true,
  hint = '',
  subheader,
  footer,
  onSave,
  onCancel,
  children,
}: BaseFormModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Scroll lock
  useEffect(() => {
    if (!scrollLock) return;
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
    }
    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open, scrollLock]);

  // Escape to close; Tab / Shift+Tab trapped within the dialog while open.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null || el === dialog);
      if (focusable.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || active == null || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || active == null || !dialog.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    },
    [onCancel, busy]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  // Focus management: on open, move focus into the dialog (first focusable, or
  // the dialog container as a fallback). On close, restore focus to whatever was
  // focused before the dialog opened.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        dialog.focus();
      }
    }
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div data-kstc-modal className={`kstc-backdrop ${theme}`}>
      <div
        ref={dialogRef}
        className={`kstc-dialog ${dialogClass}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        <div className="kstc-header">
          <h2 className="kstc-title">{title}</h2>
          <button
            className="kstc-x"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        {subheader}

        <div className={`kstc-body ${bodyClass}${busy ? ' kstc-body--disabled' : ''}`}>
          {hint && <p className="kstc-hint">{hint}</p>}
          {children}
        </div>

        {footer ? (
          <div className="kstc-actions">{footer}</div>
        ) : (
          <div className="kstc-actions">
            {showCancel && (
              <button
                className="kstc-btn kstc-btn--secondary"
                onClick={onCancel}
                disabled={busy}
                type="button"
              >
                {cancelText}
              </button>
            )}
            {showSave && (
              <button
                className={`kstc-btn ${saveButtonClass}`}
                onClick={onSave}
                disabled={saveDisabled || busy}
                type="button"
              >
                {saveText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
